import React from "react";
import { motion } from "framer-motion";
import { Timer, ArrowDown, RefreshCw } from "lucide-react";
import { fmtCountdown } from "../lib/api";

export const Hero = ({ shuffleSecs, totalVotes, onVoteJump, onShuffle }) => {
  const pct = Math.min(100, (1 - shuffleSecs / (10 * 60)) * 100);
  return (
    <section className="relative pt-28 pb-12 sm:pt-32 sm:pb-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto text-center flex flex-col items-center">
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-1.5 mb-7"
        >
          <span className="h-2 w-2 rounded-full bg-[#00ff66] animate-pulse" />
          <span className="text-xs font-semibold tracking-widest uppercase text-slate-300">
            Live · Solana Trending · 24h
          </span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="font-display font-black uppercase tracking-tighter leading-[0.9] text-5xl sm:text-7xl lg:text-8xl"
        >
          SEND <span className="neon-underline text-glow-volt">IT.</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.12 }}
          className="mt-5 text-lg sm:text-xl text-slate-300 max-w-xl"
        >
          The community decides what gets sent.{" "}
          <span className="text-white font-semibold">Every 10 minutes.</span>
        </motion.p>

        {/* countdown widget */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2 }}
          data-testid="shuffle-countdown-timer"
          className="mt-10 w-full max-w-md rounded-2xl border border-white/10 bg-[#0c0f17]/80 backdrop-blur-xl p-6"
        >
          <div className="flex items-center justify-center gap-2 text-xs font-semibold tracking-widest uppercase text-slate-400">
            <Timer size={14} className="text-[#00f0ff]" /> Next shuffle in
          </div>
          <div className="font-mono-num font-black text-6xl sm:text-7xl mt-3 tracking-tight text-glow-volt text-[#00ff66]">
            {fmtCountdown(shuffleSecs)}
          </div>
          <div className="mt-5 h-2 w-full rounded-full bg-white/10 overflow-hidden">
            <div
              className="h-full rounded-full bg-gradient-to-r from-[#00ff66] to-[#00f0ff] transition-[width] duration-1000 ease-linear"
              style={{ width: `${pct}%` }}
            />
          </div>
          <div className="mt-2 text-xs text-slate-500 font-mono-num">
            {totalVotes.toLocaleString()} total community votes cast
          </div>
        </motion.div>

        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <button
            data-testid="hero-vote-jump"
            onClick={onVoteJump}
            className="group inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-[#00ff66] to-[#00f0ff] text-black font-black uppercase tracking-wider px-7 py-3.5 hover:shadow-[0_0_28px_rgba(0,255,102,0.6)] active:scale-95 transition-[transform,box-shadow] duration-200"
          >
            Vote Now <ArrowDown size={17} className="group-hover:translate-y-0.5 transition-transform" />
          </button>
          <button
            data-testid="hero-simulate-shuffle"
            onClick={onShuffle}
            className="inline-flex items-center gap-2 rounded-xl border border-white/15 bg-white/5 text-white font-bold uppercase tracking-wider px-6 py-3.5 hover:bg-white/10 active:scale-95 transition-[transform,background-color] duration-200"
          >
            <RefreshCw size={16} /> Simulate Shuffle
          </button>
        </div>
      </div>
    </section>
  );
};
